import { isPlausibleEmail, isPlausiblePhone } from "./utils";
import { fieldKeyOf } from "./registrationFields";
import type { RegistrationField } from "./types";

export interface InfoStepValues {
  first: string;
  last: string;
  email: string;
  phone: string;
  address: string;
  zip: string;
  customFields: Record<string, string>;
}

export interface InfoStepInput {
  fields: RegistrationField[];
  values: InfoStepValues;
  /** RegistrationOptions.RequireVirtualAttendeePhone — forces phone regardless of its own flag. */
  requirePhoneForVirtual?: boolean;
  attendingVirtually?: boolean;
  interpreterOffered?: boolean;
  interpreterRequested?: boolean;
  interpreterLanguage?: string;
}

export const INTERPRETER_LANGUAGE_KEY = "interpreterLanguage";

export interface InfoStepValidation {
  canAdvance: boolean;
  messages: Record<string, string>;
  blockingReason: string | null;
}

const MIN_PHONE_DIGITS = 10;
const MAX_PHONE_DIGITS = 15;

export function validateInfoStep(input: InfoStepInput): InfoStepValidation {
  const messages: Record<string, string> = {};

  for (const field of input.fields) {
    const message = validateField(field, input);
    if (message) messages[fieldKeyOf(field)] = message;
  }

  const interpreterMessage = validateInterpreterLanguage(input);
  if (interpreterMessage) {
    messages[INTERPRETER_LANGUAGE_KEY] = interpreterMessage;
  }

  const firstMessage = Object.values(messages)[0] ?? null;

  return {
    canAdvance: Object.keys(messages).length === 0,
    messages,
    blockingReason: firstMessage
  };
}

function validateField(
  field: RegistrationField,
  input: InfoStepInput
): string | null {
  const { values } = input;

  switch (field.key) {
    case "name":
      return requiredText(values.first, field.required, "Enter your name.");
    case "firstName":
      return requiredText(values.first, field.required, "Enter your first name.");
    case "lastName":
      return requiredText(values.last, field.required, "Enter your last name.");
    case "address":
      return requiredText(values.address, field.required, "Enter your address.");
    case "zip":
      return requiredText(values.zip, field.required, "Enter your ZIP code.");
    case "email":
      return validateEmail(values.email, field.required);
    case "phone":
      return validatePhone(values.phone, phoneRequired(field, input));
    default:
      return requiredText(
        values.customFields[String(field.id)] ?? "",
        field.required,
        "This answer is required."
      );
  }
}

function validateInterpreterLanguage(input: InfoStepInput): string | null {
  if (!input.interpreterOffered || !input.interpreterRequested) return null;

  return (input.interpreterLanguage ?? "").trim()
    ? null
    : "Choose the language you need an interpreter for.";
}

function phoneRequired(field: RegistrationField, input: InfoStepInput): boolean {
  if (field.required) return true;
  return Boolean(input.requirePhoneForVirtual && input.attendingVirtually);
}

function requiredText(
  value: string,
  required: boolean,
  message: string
): string | null {
  if (!required) return null;
  return value.trim() ? null : message;
}

function validateEmail(value: string, required: boolean): string | null {
  const entered = value.trim();
  if (!entered) {
    return required ? "Enter your email address." : null;
  }

  return isPlausibleEmail(entered) ? null : "Enter a valid email address.";
}

function validatePhone(value: string, required: boolean): string | null {
  const digits = value.replace(/\D/g, "");
  if (!digits) {
    return required ? "Enter your phone number." : null;
  }

  if (digits.length < MIN_PHONE_DIGITS || digits.length > MAX_PHONE_DIGITS) {
    return `Enter a phone number with ${MIN_PHONE_DIGITS} to ${MAX_PHONE_DIGITS} digits.`;
  }

  return isPlausiblePhone(value) ? null : "Enter a valid phone number.";
}
